import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Send, MessageSquare, ChevronRight, Search } from 'lucide-react';
import { AppShell } from '../components/AppShell';
import { AppModal } from '../components/ui/AppModal';
import { PageContainer } from '../components/ui/PageContainer';
import { PageHeader } from '../components/ui/PageHeader';
import { Chip } from '../components/ui/Chip';
import { platformAPI } from '../services/api';

/* ── Static knowledge base (kept from previous implementation) ── */
const KNOWLEDGE_BASE = [
  {
    title: 'Getting started with LiveBetter Hub',
    category: 'Overview',
    readTime: '4 min read',
    content:
      'LiveBetter Hub connects upstream data pipelines (WP2) and AI-based services (WP3) and exposes them to end-user stakeholders through a secure, EU BSO-compliant infrastructure. Start by adding a building in the portfolio, connecting a data source, and exploring the WP3 service catalogue.',
  },
  {
    title: 'Publishing a verified data asset',
    category: 'Data Exchange',
    readTime: '6 min',
    content:
      'To publish a verified data asset, connect your wallet on the Data Exchange page, fill in the asset metadata (data type, building reference, validity period, price in ETH), then submit. The platform notarizes a Proof-of-Existence on-chain and registers the offer in DataAssetRegistry.',
  },
  {
    title: 'Connecting Keycloak SSO',
    category: 'Admin',
    readTime: '3 min',
    content:
      'Keycloak SSO integration is configured via environment variables KEYCLOAK_URL, KEYCLOAK_REALM, and KEYCLOAK_CLIENT_ID. The auth middleware falls back to local JWT verification when Keycloak is unavailable. See the multi-tenancy roadmap for tenant-aware realm bootstrap.',
  },
  {
    title: 'Reading smart-contract agreements',
    category: 'Blockchain',
    readTime: '8 min',
    content:
      'The DataServiceAgreement contract manages the full escrow lifecycle with seven statuses: PENDING, COUNTERSIGNED, ACTIVE, FULFILLED, DISPUTED, CANCELLED, and EXPIRED. View active agreements on the Data Exchange → Agreements tab.',
  },
  {
    title: 'EPC drift & certificate re-issuance',
    category: 'Compliance',
    readTime: '5 min',
    content:
      'When metered performance diverges from the theoretical EPC score by more than the configured threshold, the platform flags the certificate for re-issuance. Once certified EPCs from the Certification / EPC service are connected, the details of each building on the Buildings page will show this drift.',
  },
  {
    title: 'Flexibility bid workflow (DSO)',
    category: 'Operations',
    readTime: '7 min',
    content:
      'Flexibility bids are submitted to the local DSO via the Services → Activation panel. Each bid references one or more buildings and a target dispatch window. Acceptance is logged on-chain.',
  },
];

const SUGGESTIONS = [
  'Which building used the most energy last week?',
  'How do I publish a new HVAC telemetry asset?',
  'Show me active flexibility agreements',
  'What is EU-BSO and how do I export to it?',
];

const WELCOME_MESSAGE =
  "Hi 👋 I'm your LiveBetter assistant. Ask about any building, EPC certificate, agreement, or how to publish a data asset.";

/* ── Inline UI primitives ────────────────────────────────────── */
const ChatBubble = ({ role, text }) => {
  const isUser = role === 'user';
  if (isUser) {
    return (
      <div className="flex max-w-[640px] self-end">
        <div className="rounded-[12px] px-4 py-2.5 text-[14px] leading-[1.55] bg-lb-ink-strong text-white">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3 max-w-[640px] self-start">
      <div
        className="w-7 h-7 rounded-[8px] flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: '#e3f0d8', color: '#2f5a2c' }}
        aria-hidden="true"
      >
        <Sparkles size={14} strokeWidth={2} />
      </div>
      <p className="text-[14px] leading-[1.55] text-lb-ink-strong m-0">
        {text}
      </p>
    </div>
  );
};

const SuggestionChip = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-9 px-3.5 rounded-[18px] text-[13px] font-medium text-lb-ink-strong bg-white border border-[rgba(20,20,20,0.10)] hover:bg-[#f3f3ee] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#6fb96a] focus-visible:ring-offset-2"
  >
    {children}
  </button>
);

const GuideRow = ({ guide, onClick, isLast }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'group w-full text-left flex items-center gap-3 py-3 px-3.5 rounded-[8px] transition-colors duration-150 hover:bg-[rgba(20,20,20,0.03)]',
      isLast ? '' : 'border-b border-[rgba(20,20,20,0.06)]',
    ].join(' ')}
  >
    <div className="flex-1 min-w-0">
      <p className="text-[14px] font-semibold text-lb-ink-strong leading-[1.4] m-0 truncate">
        {guide.title}
      </p>
      <p className="text-[12px] text-lb-muted-soft leading-[1.4] mt-1 m-0">
        {guide.category} · {guide.readTime}
      </p>
    </div>
    <ChevronRight
      size={14}
      className="text-lb-muted-soft group-hover:text-lb-ink-strong transition-colors flex-shrink-0"
      aria-hidden="true"
    />
  </button>
);

/* ── Page ────────────────────────────────────────────────────── */
export const Wiki = () => {
  const [messages, setMessages] = useState([
    { id: 'welcome', role: 'assistant', text: WELCOME_MESSAGE },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [indexedResults, setIndexedResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const listRef = useRef(null);

  /* Auto-scroll chat to bottom on new messages */
  useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, thinking]);

  /* Indexed wiki search via platformAPI (preserved from previous implementation) */
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setIndexedResults([]);
      setSearchError('');
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await platformAPI.searchWiki(q);
        if (!cancelled) {
          setIndexedResults(response.results || []);
          setSearchError('');
        }
      } catch (error) {
        if (!cancelled) {
          setIndexedResults([]);
          setSearchError(error.message || 'Indexed search unavailable');
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  const filteredGuides = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return KNOWLEDGE_BASE;
    return KNOWLEDGE_BASE.filter(
      (g) =>
        g.title.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q) ||
        g.content.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const send = (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;

    setMessages((m) => [
      ...m,
      { id: `u-${Date.now()}`, role: 'user', text: trimmed },
    ]);
    setInput('');
    setThinking(true);

    /* Mock reply — the real assistant lives in <FloatingAssistant /> */
    setTimeout(() => {
      setMessages((m) => [
        ...m,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          text: `Based on your portfolio, "${trimmed.slice(0, 60)}${trimmed.length > 60 ? '…' : ''}" — Portside Logistics consumed 1,180 kWh/day on average (↑ 12% vs 7-day mean). Would you like to open an inspection or configure a flexibility bid?`,
        },
      ]);
      setThinking(false);
    }, 600);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    send();
  };

  const showSuggestions = messages.length <= 1;

  return (
    <AppShell title="Wiki">
      <PageContainer>
        {/* ── Header ───────────────────────────────────────────── */}
        <PageHeader
          title="Assistant & Knowledge"
          subtitle="Ask questions. Search docs. Ground answers in your portfolio data."
          actions={
            <label className="inline-flex items-center gap-2 h-10 px-3 rounded-[8px] bg-white border border-[rgba(20,20,20,0.08)] w-[320px] focus-within:border-[#6fb96a] focus-within:ring-[3px] focus-within:ring-[rgba(111,185,106,0.20)]">
              <Search size={16} className="text-lb-muted-soft flex-shrink-0" aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search documentation…"
                aria-label="Search knowledge base"
                className="flex-1 bg-transparent outline-none text-[14px] text-lb-ink-strong placeholder:text-lb-muted-soft"
              />
            </label>
          }
        />

        {/* ── Indexed search results (when typing) ─────────────── */}
        {searchQuery.trim().length >= 2 && (
          <section
            className="card-base p-4"
            aria-label="Indexed search results"
          >
            <h2 className="text-[12px] font-bold tracking-[0.07em] text-lb-muted mb-3">
              Indexed results
            </h2>
            {searchError && (
              <p className="text-[12px] text-lb-muted">
                Indexed search unavailable; showing local matches below.
              </p>
            )}
            {!searchError && indexedResults.length === 0 && (
              <p className="text-[12px] text-lb-muted">
                No indexed results for &ldquo;{searchQuery}&rdquo;.
              </p>
            )}
            {!searchError && indexedResults.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {indexedResults.map((entry) => (
                  <button
                    key={entry.id || entry.title}
                    type="button"
                    onClick={() =>
                      setSelectedGuide({
                        title: entry.title,
                        category: entry.category || 'Indexed',
                        readTime: '',
                        content: entry.content || '',
                      })
                    }
                    className="text-left card-base p-3 hover:shadow-card-hover transition-shadow duration-150"
                  >
                    <span className="text-[10px] font-bold tracking-[0.07em] text-lb-green-primary">
                      {entry.category || 'Indexed'}
                    </span>
                    <p className="text-[13px] font-bold text-lb-ink mt-1 mb-1 leading-snug">
                      {entry.title}
                    </p>
                    <p className="text-[11.5px] text-lb-muted leading-relaxed line-clamp-3">
                      {(entry.content || '').slice(0, 180)}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Two-col: chat panel + guides ─────────────────────── */}
        <section
          className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4"
          aria-label="Assistant chat and guides"
        >
          {/* Chat panel */}
          <article className="bg-white rounded-[12px] border border-[rgba(20,20,20,0.06)] shadow-lb-card flex flex-col p-0 overflow-hidden min-h-[540px]">
            {/* Header */}
            <header className="px-6 py-4 border-b flex items-center gap-3" style={{ borderColor: 'rgba(20,20,20,0.06)' }}>
              <div
                className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0"
                style={{ background: '#e3f0d8', color: '#2f5a2c' }}
                aria-hidden="true"
              >
                <Sparkles size={16} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[16px] font-semibold text-lb-ink-strong leading-[1.3] m-0 truncate">
                  LiveBetter Assistant
                </p>
                <p className="text-[13px] text-lb-muted leading-[1.4] m-0 mt-0.5">
                  Connected to your portfolio · live
                </p>
              </div>
              <Chip variant="active" dot>Online</Chip>
            </header>

            {/* Messages */}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto scrollbar-thin px-6 py-6 flex flex-col gap-5"
              aria-live="polite"
            >
              {messages.map((m) => (
                <ChatBubble key={m.id} role={m.role} text={m.text} />
              ))}
              {thinking && (
                <div className="flex gap-3 max-w-[640px] self-start">
                  <div
                    className="w-7 h-7 rounded-[8px] flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: '#e3f0d8', color: '#2f5a2c' }}
                    aria-hidden="true"
                  >
                    <Sparkles size={14} strokeWidth={2} />
                  </div>
                  <p className="text-[14px] leading-[1.55] text-lb-muted m-0 italic">Thinking…</p>
                </div>
              )}
            </div>

            {/* Suggestion chips */}
            {showSuggestions && (
              <div className="px-6 pb-4 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <SuggestionChip key={s} onClick={() => send(s)}>
                    {s}
                  </SuggestionChip>
                ))}
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="px-4 py-4 border-t flex items-center gap-3"
              style={{ borderColor: 'rgba(20,20,20,0.06)' }}
            >
              <label className="flex-1 flex items-center gap-3 h-12 px-4 rounded-[8px] bg-white border border-[rgba(20,20,20,0.10)] focus-within:border-[#6fb96a] focus-within:ring-[3px] focus-within:ring-[rgba(111,185,106,0.20)]">
                <MessageSquare size={16} className="text-lb-muted-soft flex-shrink-0" aria-hidden="true" />
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything about your portfolio…"
                  aria-label="Message"
                  className="flex-1 bg-transparent outline-none text-[15px] text-lb-ink-strong placeholder:text-lb-muted-soft"
                />
              </label>
              <button
                type="submit"
                disabled={!input.trim() || thinking}
                className="h-8 w-8 rounded-full flex items-center justify-center bg-[#0f3a23] text-white hover:bg-[#14492c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#6fb96a] focus-visible:ring-offset-2 flex-shrink-0"
                aria-label="Send message"
              >
                <Send size={14} strokeWidth={2} />
              </button>
            </form>
          </article>

          {/* Guides */}
          <aside className="bg-white rounded-[12px] border border-[rgba(20,20,20,0.06)] shadow-lb-card p-5 flex flex-col" aria-label="Guides">
            <h2 className="text-[14px] font-medium uppercase tracking-[0.04em] text-lb-muted m-0 mb-3">
              Guides
            </h2>
            {filteredGuides.length === 0 ? (
              <p className="text-[12px] text-lb-muted py-2">
                No guides match &ldquo;{searchQuery}&rdquo;.
              </p>
            ) : (
              <div className="flex flex-col">
                {filteredGuides.map((guide, i) => (
                  <GuideRow
                    key={guide.title}
                    guide={guide}
                    isLast={i === filteredGuides.length - 1}
                    onClick={() => setSelectedGuide(guide)}
                  />
                ))}
              </div>
            )}
          </aside>
        </section>

        {/* ── Guide detail modal ───────────────────────────────── */}
        <AppModal
          open={Boolean(selectedGuide)}
          title={selectedGuide?.title}
          description={selectedGuide?.category}
          onClose={() => setSelectedGuide(null)}
          maxWidth="640px"
        >
          {selectedGuide?.readTime && (
            <p className="text-[11px] font-semibold tracking-[0.07em] text-lb-muted mb-3">
              {selectedGuide.readTime}
            </p>
          )}
          <p
            style={{
              fontSize: '14px',
              lineHeight: 1.7,
              color: '#3D4D3B',
              margin: 0,
              whiteSpace: 'pre-wrap',
            }}
          >
            {selectedGuide?.content}
          </p>
        </AppModal>
      </PageContainer>
    </AppShell>
  );
};
